/**
 * **The curve and its structure, side by side — one ladder, two surfaces**
 * (VIS/V9).
 *
 * The run workspace and the Analysis tab both draw the same pairing: a
 * dihedral curve taking the width, and a rail beside it holding the conformer
 * at the scrubbed angle (plus that fit's numbers). They express it differently
 * — the workspace is a flex row with a fixed-width aside, the Analysis tab a
 * two-column grid — but the WIDTHS must be one decision, because a reader who
 * learns the shape on one surface should not re-learn it on the other at a
 * different breakpoint. Before this module they were two class lists that
 * happened to say `lg` and `22rem`, and a comment asserting they matched.
 *
 * The class strings are literals, not built by joining {@link PAIRING_STEPS}:
 * Tailwind finds classes by scanning source TEXT, so a composed
 * `` `${screen}:w-[${rem}rem]` `` produces no CSS at all. The list and the
 * literals are therefore checked against each other in
 * `torsion-fit-pairing.test.ts` rather than derived from each other.
 */

/** The `w-56` sidebar, at its expanded width — the wider of its two states. */
export const SIDEBAR_PX = 224;
/** One `--gutter` inset; `<main>` carries two of them. */
export const GUTTER_PX = 20;
/** The `gap-4` between the visual and the rail. */
export const GAP_PX = 16;

/**
 * The narrowest a focused dihedral chart may be and still be read.
 *
 * Argued in `RunWorkspace.tsx`: axis labels, the legend, and two co-plotted
 * series that must stay distinguishable. It is the FLOOR the rail widths are
 * chosen against — below it a chart is not merely cramped, so the pairing
 * stacks instead of shrinking past it.
 */
export const MIN_READABLE_CHART_PX = 392;

/**
 * The ladder, narrowest first: at each screen, how wide the rail is.
 *
 * `pair` is this repo's own breakpoint (`tailwind.config.js` derives it);
 * `xl` and `2xl` are Tailwind's. Below `pair` there is no step, because the
 * pairing stacks there rather than continuing to narrow.
 */
export const PAIRING_STEPS = [
  { screen: "pair", minWidthPx: 960, railRem: 18 },
  { screen: "xl", minWidthPx: 1280, railRem: 22 },
  { screen: "2xl", minWidthPx: 1536, railRem: 26 },
] as const;

/** The run workspace's rail wrapper: stacked below `pair`, a column above it. */
export const PAIRING_RAIL_CLASS = "w-full pair:w-[18rem] pair:shrink-0 xl:w-[22rem] 2xl:w-[26rem]";

/**
 * The Analysis tab's equivalent, as a grid template.
 *
 * `minmax(0, 1fr)` rather than a bare `1fr` for the chart column: `1fr` is
 * `minmax(auto, 1fr)`, which refuses to shrink below its content and lets a
 * wide plot push the rail off the row — the grid-track form of the same trap
 * `min-w-0` guards against in flex. `items-start` so the rail does not stretch
 * to the chart's height and leave the structure floating in an over-tall box.
 */
export const PAIRING_GRID_CLASS =
  "grid grid-cols-1 items-start gap-4 pair:grid-cols-[minmax(0,1fr)_18rem] " +
  "xl:grid-cols-[minmax(0,1fr)_22rem] 2xl:grid-cols-[minmax(0,1fr)_26rem]";

/**
 * What the visual is left with at a viewport width, given the rail beside it.
 *
 * `<main>` is the viewport less the sidebar and two gutters; the rail and the
 * gap come out of what remains. Stated as arithmetic rather than as prose so a
 * test can hold the ladder to {@link MIN_READABLE_CHART_PX} instead of a reader
 * re-doing the sums from a comment.
 */
export function mainPaneWidthPx(viewportPx: number, railRem: number): number {
  return viewportPx - SIDEBAR_PX - 2 * GUTTER_PX - railRem * 16 - GAP_PX;
}
